import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Share2, Search, X, Check, Loader2, Users } from 'lucide-react';
import { useSearchUsers, useShareCasoMutation, useSharedUsers, useUnshareCasoMutation, useGlobalShareCount } from '@/hooks/useShareCaso';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDocumentPermissions } from '@/hooks/useDocumentPermissions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useGroupsManagement } from '@/hooks/useGroupsManagement';
import { useShareCasoGroupMutation } from '@/hooks/useShareCasoGroup';
import { useSharedCasoGroups } from '@/hooks/useSharedCasoGroups';
import { useUnshareCasoGroupMutation } from '@/hooks/useUnshareGroup';

interface ShareCasoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  casoId: string;
  casoTitle: string;
}

export function ShareCasoDialog({ open, onOpenChange, casoId, casoTitle }: ShareCasoDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [shareMode, setShareMode] = useState<'individual' | 'group' | null>(null);

  const { canShare, shareLimit, planType } = useDocumentPermissions();
  const { data: searchResults, isLoading: isSearching } = useSearchUsers(searchTerm);
  const { data: sharedUsers, isLoading: isLoadingShared } = useSharedUsers(casoId);
  const { data: sharedGroups } = useSharedCasoGroups(casoId);
  const shareMutation = useShareCasoMutation();
  const unshareMutation = useUnshareCasoMutation();
  const { data: globalShareCount } = useGlobalShareCount();
  const { groups } = useGroupsManagement();
  const shareGroupMutation = useShareCasoGroupMutation();
  const unshareGroupMutation = useUnshareCasoGroupMutation();

  const currentShareCount = (sharedUsers?.length || 0) + (sharedGroups?.reduce((acc, g) => acc + g.member_count, 0) || 0);
  const remainingSlots = shareLimit === -1 ? Infinity : Math.max(0, shareLimit - (globalShareCount || 0));
  const canAddMore = shareLimit === -1 || remainingSlots > 0;

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleShare = async () => {
    if (selectedUserIds.length === 0) return;

    await shareMutation.mutateAsync({
      casoId,
      userIds: selectedUserIds,
      permissionLevel: 'view',
    });

    setSelectedUserIds([]);
    setSearchTerm('');
  };

  const handleUnshare = async (userId: string) => {
    await unshareMutation.mutateAsync({ casoId, userId });
  };

  const handleShareWithGroup = async () => {
    if (!selectedGroupId) return;

    try {
      await shareGroupMutation.mutateAsync({
        casoId,
        groupId: selectedGroupId,
      });
      setSelectedGroupId('');
      setShareMode(null);
    } catch (error) {
      console.error('Erro ao compartilhar com grupo:', error);
    }
  };

  const handleUnshareGroup = async (groupId: string) => {
    await unshareGroupMutation.mutateAsync({ casoId, groupId });
  };

  const getInitials = (name: string | null, email: string) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return email.slice(0, 2).toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Compartilhar Caso
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Caso Info */}
          <div className="bg-muted/50 p-3 rounded-md">
            <p className="text-sm font-medium text-foreground">
              Caso: {casoTitle}
            </p>
          </div>

          {/* Informações do plano e limites */}
          <div className={`p-3 rounded-md border ${
            !canShare ? 'bg-destructive/10 border-destructive' :
            remainingSlots === 0 ? 'bg-red-50 dark:bg-red-950/20 border-red-500 dark:border-red-800' :
            'bg-blue-50 dark:bg-blue-950/20 border-blue-300 dark:border-blue-800'
          }`}>
            {!canShare ? (
              <>
                <p className="text-sm font-semibold text-destructive">🔒 Compartilhamento bloqueado</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Seu plano <strong>{planType.toUpperCase()}</strong> não permite compartilhamento. 
                  Faça upgrade para o plano <strong>Básico</strong> ou superior.
                </p>
              </>
            ) : shareLimit === -1 ? (
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {currentShareCount > 0 ? (
                  <>✅ Compartilhamento: <strong>{currentShareCount} {currentShareCount === 1 ? 'pessoa' : 'pessoas'}</strong> (Ilimitado no plano Premium)</>
                ) : (
                  <>✅ (Ilimitado no plano Premium)</>
                )}
              </p>
            ) : (
              <>
                <p className="text-sm font-semibold">
                  📊 Compartilhamentos: <strong>{currentShareCount}/{shareLimit}</strong>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {remainingSlots === 0 ? (
                    <span className="text-red-600 dark:text-red-400 font-semibold">
                      ⚠️ Limite atingido! Você já usou todos os compartilhamentos disponíveis (casos + chats).
                      <br />
                      Remova alguém ou faça upgrade para o plano Pro (10) ou Premium (ilimitado).
                    </span>
                  ) : (
                    <span>
                      Total global de compartilhamentos (casos + chats). Restam <strong>{remainingSlots}</strong>.
                    </span>
                  )}
                </p>
              </>
          )}
          </div>

          {/* Cards de Seleção de Modo */}
          {!shareMode && (
            <div className="grid grid-cols-2 gap-4">
              {/* Card Individual */}
              <button 
                onClick={() => setShareMode('individual')}
                className="p-6 border-2 rounded-lg hover:border-primary hover:bg-accent/50 transition-all text-center space-y-3"
              >
                <div className="flex justify-center">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Search className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Compartilhamento Individual</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Buscar e selecionar usuários específicos
                  </p>
                </div>
              </button>
              
              {/* Card Grupo */}
              <button 
                onClick={() => setShareMode('group')}
                className="p-6 border-2 rounded-lg hover:border-primary hover:bg-accent/50 transition-all text-center space-y-3 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!groups || groups.length === 0}
              >
                <div className="flex justify-center">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Compartilhar com Grupo</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {groups && groups.length > 0 
                      ? 'Compartilhar com todos os membros de um grupo'
                      : 'Nenhum grupo criado ainda'
                    }
                  </p>
                </div>
              </button>
            </div>
          )}

          {/* Compartilhar com Grupo */}
          {shareMode === 'group' && groups && groups.length > 0 && (
            <>
              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <label className="text-sm font-semibold">Compartilhar com Grupo</label>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Selecione um grupo para compartilhar com todos os membros de uma vez
                </p>
                
                <Select 
                  value={selectedGroupId} 
                  onValueChange={setSelectedGroupId}
                  disabled={!canShare || !canAddMore}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um grupo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {groups
                      ?.filter(g => !sharedGroups?.some(sg => sg.id === g.id))
                      .map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name} ({group.member_count} {group.member_count === 1 ? 'membro' : 'membros'})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={handleShareWithGroup}
                  disabled={!selectedGroupId || !canShare || !canAddMore || shareGroupMutation.isPending}
                  className="w-full"
                >
                  {shareGroupMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Compartilhando...
                    </>
                  ) : (
                    <>
                      <Users className="h-4 w-4 mr-2" />
                      Compartilhar com Grupo
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {/* Search Users */}
          {shareMode === 'individual' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Buscar Usuário</label>
                <p className="text-xs text-muted-foreground">
                  Digite o e-mail completo do usuário para compartilhar
                </p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Digite o e-mail completo (ex: usuario@email.com)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                    className="pl-9"
                    type="email"
                    disabled={!canShare || !canAddMore}
                  />
                </div>

                {/* Search Results */}
                {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(searchTerm) && (
                  <ScrollArea className="h-[200px] rounded-md border p-2">
                    {isSearching ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : searchResults?.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        Nenhum usuário encontrado
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {searchResults?.map((user) => {
                          const isSelected = selectedUserIds.includes(user.id);
                          const isAlreadyShared = sharedUsers?.some(s => s.id === user.id);

                          return (
                            <button
                              key={user.id}
                              onClick={() => !isAlreadyShared && handleToggleUser(user.id)}
                              disabled={isAlreadyShared}
                              className={`
                                w-full flex items-center gap-3 p-2 rounded-md transition-colors
                                ${isAlreadyShared ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted cursor-pointer'}
                                ${isSelected ? 'bg-primary/10 border border-primary' : ''}
                              `}
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={user.avatar_url || undefined} />
                                <AvatarFallback className="text-xs">
                                  {getInitials(user.display_name, user.email)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 text-left">
                                <p className="text-sm font-medium">{user.display_name || 'Sem nome'}</p>
                                <p className="text-xs text-muted-foreground">{user.email}</p>
                              </div>
                              {isAlreadyShared && (
                                <Badge variant="secondary" className="text-xs">Já compartilhado</Badge>
                              )}
                              {isSelected && !isAlreadyShared && (
                                <Check className="h-4 w-4 text-primary" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                )}
              </div>

              {/* Selected Users */}
              {selectedUserIds.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Selecionados ({selectedUserIds.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedUserIds.map(userId => {
                      const user = searchResults?.find(u => u.id === userId);
                      if (!user) return null;
                      return (
                        <Badge key={userId} variant="secondary" className="gap-1">
                          {user.display_name || user.email}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => handleToggleUser(userId)}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Grupos com Acesso */}
          {sharedGroups && sharedGroups.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Grupos com Acesso ({sharedGroups.length})
              </label>
              <ScrollArea className="max-h-[150px] rounded-md border p-2">
                <div className="space-y-1">
                  {sharedGroups.map((group) => (
                    <div
                      key={group.id}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                    >
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{group.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {group.member_count} {group.member_count === 1 ? 'membro' : 'membros'}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnshareGroup(group.id)}
                        disabled={unshareGroupMutation.isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Already Shared Users */}
          {sharedUsers && sharedUsers.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Com Acesso ({sharedUsers.length})</label>
              <ScrollArea className="max-h-[150px] rounded-md border p-2">
                <div className="space-y-1">
                  {sharedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatar_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {getInitials(user.display_name, user.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{user.display_name || 'Sem nome'}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnshare(user.id)}
                        disabled={unshareMutation.isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          {shareMode && (
            <Button 
              variant="outline" 
              onClick={() => setShareMode(null)}
            >
              ← Voltar
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {shareMode === 'individual' && (
            <Button
              onClick={handleShare}
              disabled={!canShare || !canAddMore || selectedUserIds.length === 0 || shareMutation.isPending}
            >
              {!canShare ? (
                <>🔒 Bloqueado (Plano {planType.toUpperCase()})</>
              ) : shareMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Compartilhando...
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartilhar ({selectedUserIds.length})
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
